// =====================================================================
// Cockpit WS protocol — the browser side of the SDR calling flow.
//
// Hand-rolled mirror of the BFF's canonical contract (apps/bff/src/cockpit/
// protocol.ts), kept in lockstep BY HAND — exactly like src/bff.ts mirrors the
// BFF's ping/outcome contract. (Neither SPA imports @maestro/shared at
// build/test time; both duplicate their wire types as plain framework-free TS
// so the message logic is unit-tested without a socket.) Cockpit frames share
// the one BFF socket with ping/pong + dialer_outcome.
// =====================================================================

import { JOINABLE_POOL_STATES } from '@/lib/pool-lifecycle';

export type Disposition = 'rfi' | 'meeting_booked' | 'dnc';

export interface PoolInfo {
  id: string;
  name: string;
  color: string;
  waiting: number;
  // Lifecycle state (draft/open/calling/paused/draining/closed). The picker badges
  // it and only enables "join" for a joinable (open/calling/paused) pool — see
  // isPoolJoinable. `calling` = SDRs are actively dialing the pool right now.
  status: string;
  // Pool lead progress — total leads and how many are still to dial. Deliberately
  // rendered nowhere in the cockpit: an SDR watching the pool drain reads it as a
  // quota. Still sent by the BFF (it's operational data, not a secret), so this is
  // a display decision, not a redaction. Optional so a partial/older frame still
  // type-checks.
  leadCount?: number;
  remainingLeads?: number;
  // The clients this pool dials for (contact-derived, most leads first) — a pool
  // can span several clients. Optional so a partial/older frame still type-checks.
  clientNames?: string[];
  // ISO8601 instant a finished pool becomes joinable again, present only while a
  // `closed` pool waits out its leads' ring ladder. An absolute instant, not a
  // remaining duration: the pools frame is re-sent every few seconds, so a
  // server-computed remainder would jitter and could tick backwards. The card counts
  // down from it locally.
  redialableAt?: string | null;
  // Whether joining now would trigger the redial. Server-decided, so a fast workstation
  // clock cannot enable the card before the core would accept the join.
  redialDue?: boolean;
  // Present (non-null) only while the pool is waiting on working hours — a WAITING
  // condition, not a lifecycle pause (`status` stays `open`). The asleep-lead count,
  // the timezone makeup, and the soonest resume let a joined SDR see why nothing is
  // dialing. Optional so a partial/older frame still type-checks.
  offHours?: OffHours | null;
}

// The pool's off-hours snapshot (see PoolInfo.offHours). `soonestResume.at` is an
// ISO8601 instant — the earliest moment a currently-asleep zone opens. Rendering it
// as a local time needs `timezone`, since zones don't all open at the same hour;
// absent from an older core, in which case only the countdown is shown.
export interface OffHours {
  count: number;
  timezones: { timezone: string; label: string; count: number }[];
  soonestResume: { label: string; timezone?: string; at: string } | null;
  // ISO8601 deadline of the dark-pool eviction, present only while the grace timer
  // is armed for the pool the SDR is seated in — the banner warns them they are
  // about to be returned to the picker. Optional: older BFF/core payloads omit it.
  evictingAt?: string | null;
  // The other population: leads awake but resting on their ring budget, and when the
  // first returns. `count` above is asleep behind a window — a different wait, never
  // summed with this one.
  resting: number;
  nextBatchAt: string | null;
  reason: string;
  // The earlier of the two returns, decided by the core so every surface counts down to
  // the same moment.
  nextReturnAt: string | null;
}

// A missing status (older BFF) is treated as joinable, matching pre-lifecycle behavior.
export function isPoolJoinable(status: string | undefined): boolean {
  return status === undefined || (JOINABLE_POOL_STATES as readonly string[]).includes(status);
}

// The mutual-silence gate's per-card config (plans/silence-cutoff/plan.md),
// stamped by the core on every card push so a console flip lands on the NEXT
// call with no deploy. `mode` stays an open string here; cockpit/silence-gate.ts
// gateMode() degrades anything unrecognised to 'off'.
export interface SilenceGateConfig {
  mode: string;
  floorMs: number;
  quietMs: number;
  // The lead-only tier's window (fires on lead silence alone, regardless of SDR
  // audio). Null disables that tier — an older BFF/core simply doesn't send it.
  leadQuietMs: number | null;
  graceMs: number;
  remoteMax: number;
  localMin: number;
}

// Whether there is a calendar to show for this call. 'unsupported' means nobody
// publishes one for this client — a settled answer, not a wait. 'unavailable'
// means one exists but this lookup failed, so it is worth saying so rather than
// letting the SDR think the seller has no time.
export type CalendarStatus = 'unsupported' | 'unavailable' | 'ready';

// One open slot. Absolute instants, never a formatted local time: which day a
// slot falls on depends on the zone it is read in, so both grouping and
// formatting happen wherever that zone is chosen.
export interface AvailabilitySlot {
  startUtc: string;
  endUtc: string;
  // Present only in the unmatched shape, where slots are a union across sellers.
  // A slot free for one of seven is a much weaker suggestion than one free for
  // all seven, and the weak ones are what get rescheduled.
  freeSellerCount?: number;
}

// Two shapes in one. `matched` true means a single seller owns this account and
// the slots are theirs. False means nobody could be identified and the slots are
// every seller's time unioned — a different, weaker claim that has to read
// differently. `resolvedSellerName` can be set while `matched` is false: the
// owner is known, but there is no calendar for them.
export interface SellerAvailability {
  matched: boolean;
  matchedBy: 'domain' | 'state' | 'city' | 'none';
  resolvedSellerName: string | null;
  seller: { name: string; timeZone: string } | null;
  sellers: { name: string; timeZone: string }[] | null;
  window: { start: string; end: string };
  // The upstream snapshot is old enough that the times may have moved on. Shown
  // to the SDR rather than used to withhold the calendar.
  stale: boolean;
  slots: AvailabilitySlot[];
}

export interface LeadCall {
  callId: string;
  poolId: string;
  contactName: string;
  company: string;
  phone: string; // E.164
  timezone: string; // IANA
  title: string | null;
  // Rich lead fields (from the contact's metadata, populated by the throxy import)
  // so the SDR card shows who they're talking to. Null when the contact has none.
  persona: string | null;
  location: string | null; // "City, Country"
  linkedinUrl: string | null;
  email: string | null;
  // The lead's client throxy_client_id (shared cross-service id), core-resolved.
  // Used for the "open in leadflow" talk-track fallback link.
  throxyClientId: string | null;
  // The lead's client display name — always on the card, so the SDR knows which
  // client they're speaking for (a pool can span several clients).
  clientName: string | null;
  // A real AMD `human` verdict backs this card, rather than the core's watchdog
  // guessing one before the verdict lands. Gates the silent-voicemail guard.
  confirmed: boolean;
  // The card badges 'inbound', so an SDR is never surprised by a returned call.
  origin: 'outbound' | 'inbound';
  // The silence-gate config governing this card; null (gate off) from an older
  // BFF/core or a malformed object — the gate must fail toward doing nothing.
  silenceGate: SilenceGateConfig | null;
}

// The SDR's Twilio Voice access token, minted BFF-side and pushed over the WS
// (the browser never fetches it). `token` is null until real Twilio creds are
// provisioned, which tells the device to run simulated.
export interface VoiceToken {
  token: string | null;
  identity: string;
  expiresAt: string;
}

// --- Outbound: client -> BFF ---
export type CockpitClientFrame =
  | { kind: 'list_pools' }
  | { kind: 'join_pool'; poolId: string }
  | { kind: 'end_call'; callId: string; disposition?: Disposition }
  | { kind: 'leave_pool'; poolId: string }
  // The SDR gave up on a drain that never finished. Clears BFF-side state so the
  // session doesn't look mid-leave forever; the core releases them on the next join.
  | { kind: 'abandon_leave'; poolId: string }
  | { kind: 'refresh_voice_token' }
  // The softphone raised its silent-voicemail banner. Informational telemetry only —
  // the call stays live (contrast call_manually_dropped). callId is the active lead.
  | { kind: 'voicemail_prompt_raised'; callId: string }
  // The SDR MANUALLY dropped this call (AMD misclassified it as human: the card
  // was shown but it's a machine, dead air, IVR, etc.). Distinct from the
  // `voicemail_prompt_raised` telemetry so the BFF can count AMD misses (ground
  // truth) separately; this one stops the call + returns to pool. callId is active lead.
  | { kind: 'call_manually_dropped'; callId: string }
  // The silence gate reported an event (cockpit/silence-gate.ts): `raised` (the
  // prompt fired), `stayed` (dismissed: Continue clicked, a side made a sound, or
  // the call was disposed), `cut` (the countdown expired; the SPA's own end_call
  // follows separately). The numbers exist only at the moment of the decision, so
  // they ride the frame. callId is the active lead.
  | {
      kind: 'silence_gate';
      callId: string;
      action: 'raised' | 'stayed' | 'cut';
      mode?: string;
      rule?: 'mutual' | 'lead_only';
      how?: 'clicked' | 'sound' | 'disposed';
      cardAgeMs?: number;
      quietMs?: number;
      reactionMs?: number;
      countdownMs?: number;
      // Whether the tab was foregrounded, carried on `raised` and `cut` but never on
      // `stayed`. The pair separates a prompt nobody was looking at from one somebody
      // watched expire. Not the same as having the window in front of you: a tab behind
      // another window still reads `visible`.
      visibilityState?: 'visible' | 'hidden';
    }
  // Audio is live but no card arrived (a lost/late core push): ask the BFF to re-push the
  // current card from the snapshot. Sent by the reconcile loop in routes/cockpit.tsx
  // while Twilio is `on_call` but state.currentCall is still null.
  | { kind: 'resync_call' }
  // This socket runs the cockpit, so a person can be expected to answer for it.
  // Claimed per socket, so a reconnect has to claim again; idempotent, because it is
  // sent more than once to survive the window before the server can record it.
  | { kind: 'presence_hello' }
  // Somebody did something in this tab. Payload-less — the server stamps the time.
  | { kind: 'presence_beat' }
  // The SDR answered the "still there?" prompt.
  | { kind: 'presence_ack' }
  // The softphone hit a device/call error. Attribution only — the leg is already gone; this
  // says WHY, so a browser fault is not filed as the far end hanging up.
  | { kind: 'client_error'; callId: string; code: number | null };

export const listPoolsFrame = (): CockpitClientFrame => ({ kind: 'list_pools' });
export const refreshVoiceTokenFrame = (): CockpitClientFrame => ({ kind: 'refresh_voice_token' });
export const joinPoolFrame = (poolId: string): CockpitClientFrame => ({
  kind: 'join_pool',
  poolId,
});
export const clientErrorFrame = (callId: string, code: number | null): CockpitClientFrame => ({
  kind: 'client_error',
  callId,
  code,
});
// No disposition: the SDR asserts an EVENT (they ended the call), never a label —
// dispositions stay the LLM's (Decision 3). The field remains in the contract type
// for a future CRM-sourced writer; nothing in the SPA produces it.
export const endCallFrame = (callId: string): CockpitClientFrame => ({
  kind: 'end_call',
  callId,
});
export const leavePoolFrame = (poolId: string): CockpitClientFrame => ({
  kind: 'leave_pool',
  poolId,
});
export const abandonLeaveFrame = (poolId: string): CockpitClientFrame => ({
  kind: 'abandon_leave',
  poolId,
});
export const voicemailPromptRaisedFrame = (callId: string): CockpitClientFrame => ({
  kind: 'voicemail_prompt_raised',
  callId,
});
export const callManuallyDroppedFrame = (callId: string): CockpitClientFrame => ({
  kind: 'call_manually_dropped',
  callId,
});
// The silence-gate receipt. The event's numbers are already rounded ints by the
// time they get here (useTwilioPhone rounds at capture).
export const silenceGateFrame = (
  callId: string,
  event: Omit<Extract<CockpitClientFrame, { kind: 'silence_gate' }>, 'kind' | 'callId'>,
): CockpitClientFrame => ({
  kind: 'silence_gate',
  callId,
  ...event,
});
export const resyncCallFrame = (): CockpitClientFrame => ({ kind: 'resync_call' });
export const presenceHelloFrame = (): CockpitClientFrame => ({ kind: 'presence_hello' });
export const presenceBeatFrame = (): CockpitClientFrame => ({ kind: 'presence_beat' });
export const presenceAckFrame = (): CockpitClientFrame => ({ kind: 'presence_ack' });

// --- Inbound: BFF -> client ---
export type CockpitServerFrame =
  // seatHeld is REQUIRED on the parsed frame (parse normalizes absence to
  // false) so consumers cannot half-handle an undefined that never occurs.
  | { v: 1; kind: 'pools'; pools: PoolInfo[]; seatHeld: boolean }
  | { v: 1; kind: 'voice_token'; voice: VoiceToken }
  | { v: 1; kind: 'pool_joined'; poolId: string }
  | { v: 1; kind: 'lead_assigned'; call: LeadCall }
  // A guessed card is now backed by a real AMD `human` verdict. Its own frame because
  // a re-pushed lead_assigned would clear the talk track. Keyed by callId: the SDR can
  // have several legs bridged at once, so this may name a card we aren't showing.
  | { v: 1; kind: 'lead_confirmed'; callId: string }
  // The connected lead's client talk track, sent AFTER lead_assigned (so a slow
  // fetch never delays the card). Keyed by callId so the reducer can drop a late
  // frame for a since-ended call. Absent => the panel shows the fallback link.
  | { v: 1; kind: 'talk_track'; callId: string; name: string; markdown: string }
  // The seller who owns this lead's account and when they're free, sent AFTER
  // lead_assigned on the same terms as the talk track. `status` separates the two
  // silences: 'unsupported' is a settled no, 'unavailable' is a lookup that failed
  // for a client that does have a calendar. Collapsing them would either promise
  // one that never comes or hide an outage.
  | {
      v: 1;
      kind: 'lead_calendar';
      callId: string;
      status: CalendarStatus;
      calendar: SellerAvailability | null;
    }
  // One of the SDR's legs ended — like lead_confirmed, it may name a card we aren't
  // showing. Match callId before acting: clearing on a sibling's end hangs up the live call.
  | { v: 1; kind: 'call_ended'; callId: string; disposition: Disposition | null }
  // `reason` names an exit the SDR did not ask for, so the picker can say why they
  // are back on it. Absent on an ordinary leave.
  //
  // `refusedReason` is present only when the CORE refused the join (409) rather than the
  // SDR or the pool ending the session, and carries the core's own word for it. Without
  // it a refused SDR lands back on the picker with no explanation — the pool list is
  // polled, so the card that sent them may still read joinable for a second or two.
  | {
      v: 1;
      kind: 'pool_left';
      poolId: string;
      reason?: 'idle' | 'cap' | 'silence_gate';
      refusedReason?: string;
    }
  // Nobody has shown a sign of life and the seat is about to be given up. `expiresAt`
  // is absolute: the frame can be re-sent, and a server-computed remainder jitters
  // with delivery latency.
  | { v: 1; kind: 'presence_warning'; trigger: 'idle' | 'cap'; expiresAt: string }
  | { v: 1; kind: 'presence_clear' }
  // The SDR clicked exit but had calls in flight, so the core is DRAINING them
  // (never cutting a pending call off). The cockpit shows a "wait for pending
  // calls" popup; a pool_left follows once the drain finishes.
  | { v: 1; kind: 'pool_leaving'; poolId: string; pendingCalls: number }
  // The core paused dialing for a graceful deploy drain: no new calls start while
  // the node finishes its in-flight calls and is replaced. Advisory — the SDR keeps
  // its pool and any live call; the cockpit shows a "deploying" banner.
  | { v: 1; kind: 'deploying' }
  // Sent on (re)connect when the core still has this SDR's session: restore them to
  // their pool + in-flight call instead of the picker. `currentCall` null = in a pool
  // but not on a call.
  | { v: 1; kind: 'session_restored'; poolId: string; currentCall: LeadCall | null }
  // The BFF task is shutting down (a deploy): reconnect to the healthy new task, which
  // re-hydrates our pool + call, so the swap is invisible instead of a dropped socket.
  | { v: 1; kind: 'reconnect'; reason: 'deploy' };

// Tolerant parse: returns the cockpit frame a raw WS message carries, or null
// for any other kind (hello / pong / dialer_outcome) or malformed input — like
// parsePong / parseOutcome in src/bff.ts. A bad frame must never crash the UI.
export function parseCockpitFrame(raw: unknown): CockpitServerFrame | null {
  if (typeof raw !== 'string') return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null) return null;
  const f = json as Record<string, unknown>;
  switch (f.kind) {
    case 'pools':
      return Array.isArray(f.pools)
        ? {
            v: 1,
            kind: 'pools',
            pools: f.pools as PoolInfo[],
            // The seat-hold capability: only an explicit true counts — an older
            // BFF omits it and the SPA keeps today's leave-first advice.
            seatHeld: f.seatHeld === true,
          }
        : null;
    case 'voice_token': {
      const voice = f.voice;
      return isVoiceToken(voice) ? { v: 1, kind: 'voice_token', voice } : null;
    }
    case 'pool_joined':
      return typeof f.poolId === 'string' ? { v: 1, kind: 'pool_joined', poolId: f.poolId } : null;
    case 'lead_assigned': {
      // Validate the required base, then normalize so the optional rich fields
      // default to null — tolerant of an older BFF that omits them.
      return isLeadCall(f.call)
        ? { v: 1, kind: 'lead_assigned', call: normalizeLeadCall(f.call) }
        : null;
    }
    case 'lead_confirmed':
      return typeof f.callId === 'string'
        ? { v: 1, kind: 'lead_confirmed', callId: f.callId }
        : null;
    case 'talk_track':
      // All three fields must be strings; a malformed frame is dropped (tolerant
      // parse). callId lets the reducer ignore a track for a since-ended call.
      return typeof f.callId === 'string' &&
        typeof f.name === 'string' &&
        typeof f.markdown === 'string'
        ? { v: 1, kind: 'talk_track', callId: f.callId, name: f.name, markdown: f.markdown }
        : null;
    case 'lead_calendar': {
      if (typeof f.callId !== 'string') return null;
      if (!isCalendarStatus(f.status)) return null;
      // A 'ready' frame whose payload didn't survive the wire is dropped to
      // 'unavailable' rather than rendered half-built — the pane then says the
      // lookup failed, which is true, instead of showing an empty calendar the
      // SDR would read as "no time free".
      const calendar = normalizeAvailability(f.calendar);
      return {
        v: 1,
        kind: 'lead_calendar',
        callId: f.callId,
        status: f.status === 'ready' && calendar === null ? 'unavailable' : f.status,
        calendar,
      };
    }
    case 'call_ended': {
      // disposition is null when the call ended without an SDR disposition (the
      // contact hung up / the core ended it). Coerce any non-Disposition to null.
      if (typeof f.callId !== 'string') return null;
      const disposition = isDisposition(f.disposition) ? f.disposition : null;
      return { v: 1, kind: 'call_ended', callId: f.callId, disposition };
    }
    case 'pool_left': {
      if (typeof f.poolId !== 'string') return null;
      const reason =
        f.reason === 'idle' || f.reason === 'cap' || f.reason === 'silence_gate'
          ? f.reason
          : undefined;
      return {
        v: 1,
        kind: 'pool_left',
        poolId: f.poolId,
        ...(reason ? { reason } : {}),
        ...(typeof f.refusedReason === 'string' ? { refusedReason: f.refusedReason } : {}),
      };
    }
    case 'presence_warning':
      // The deadline has to be a real instant, not merely a string: an unparseable one
      // renders as a countdown already at zero, which is indistinguishable from a seat
      // about to be lost.
      return (f.trigger === 'idle' || f.trigger === 'cap') &&
        typeof f.expiresAt === 'string' &&
        Number.isFinite(Date.parse(f.expiresAt))
        ? { v: 1, kind: 'presence_warning', trigger: f.trigger, expiresAt: f.expiresAt }
        : null;
    case 'presence_clear':
      return { v: 1, kind: 'presence_clear' };
    case 'pool_leaving':
      return typeof f.poolId === 'string'
        ? {
            v: 1,
            kind: 'pool_leaving',
            poolId: f.poolId,
            pendingCalls: typeof f.pendingCalls === 'number' ? f.pendingCalls : 0,
          }
        : null;
    case 'deploying':
      return { v: 1, kind: 'deploying' };
    case 'session_restored':
      // Restore to a pool + (optional) live call. currentCall is null (in a pool, no
      // call) or a full lead card; a malformed card degrades to null.
      return typeof f.poolId === 'string'
        ? {
            v: 1,
            kind: 'session_restored',
            poolId: f.poolId,
            currentCall: isLeadCall(f.currentCall) ? normalizeLeadCall(f.currentCall) : null,
          }
        : null;
    case 'reconnect':
      // Only the known 'deploy' reason; an unknown reason is dropped (tolerant parse).
      return f.reason === 'deploy' ? { v: 1, kind: 'reconnect', reason: 'deploy' } : null;
    default:
      return null;
  }
}

export function isDisposition(v: unknown): v is Disposition {
  return v === 'rfi' || v === 'meeting_booked' || v === 'dnc';
}

function isVoiceToken(v: unknown): v is VoiceToken {
  if (typeof v !== 'object' || v === null) return false;
  const t = v as Record<string, unknown>;
  return (
    (typeof t.token === 'string' || t.token === null) &&
    typeof t.identity === 'string' &&
    typeof t.expiresAt === 'string'
  );
}

// Validates only the required base fields (the rich lead fields are optional and
// normalized in normalizeLeadCall). A bad/partial frame returns false → ignored.
function isCalendarStatus(v: unknown): v is CalendarStatus {
  return v === 'unsupported' || v === 'unavailable' || v === 'ready';
}

function toSeller(v: unknown): { name: string; timeZone: string } | null {
  if (typeof v !== 'object' || v === null) return null;
  const s = v as Record<string, unknown>;
  // Both or neither: a seller with no zone can't be rendered on any clock.
  return typeof s.name === 'string' && typeof s.timeZone === 'string'
    ? { name: s.name, timeZone: s.timeZone }
    : null;
}

// Coerce a raw calendar payload, dropping anything malformed rather than letting
// it reach a component. Slots without both instants are discarded individually:
// one bad row shouldn't cost the SDR the whole calendar.
function normalizeAvailability(v: unknown): SellerAvailability | null {
  if (typeof v !== 'object' || v === null) return null;
  const a = v as Record<string, unknown>;
  const window = a.window as Record<string, unknown> | undefined;
  if (typeof window?.start !== 'string' || typeof window.end !== 'string') return null;

  const slots = Array.isArray(a.slots)
    ? a.slots.flatMap((raw): AvailabilitySlot[] => {
        if (typeof raw !== 'object' || raw === null) return [];
        const s = raw as Record<string, unknown>;
        if (typeof s.startUtc !== 'string' || typeof s.endUtc !== 'string') return [];
        return [
          {
            startUtc: s.startUtc,
            endUtc: s.endUtc,
            ...(typeof s.freeSellerCount === 'number'
              ? { freeSellerCount: s.freeSellerCount }
              : {}),
          },
        ];
      })
    : [];

  const matchedBy = a.matchedBy;
  return {
    matched: a.matched === true,
    matchedBy:
      matchedBy === 'domain' || matchedBy === 'state' || matchedBy === 'city' ? matchedBy : 'none',
    resolvedSellerName: typeof a.resolvedSellerName === 'string' ? a.resolvedSellerName : null,
    seller: toSeller(a.seller),
    sellers: Array.isArray(a.sellers)
      ? a.sellers.map(toSeller).filter((s): s is { name: string; timeZone: string } => s !== null)
      : null,
    window: { start: window.start, end: window.end },
    stale: a.stale === true,
    slots,
  };
}

function isLeadCall(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.callId === 'string' &&
    typeof c.poolId === 'string' &&
    typeof c.contactName === 'string'
  );
}

// Coerce a validated raw lead into a fully-typed LeadCall: required string fields
// fall back to '' (isLeadCall already vetted callId/poolId/contactName), and the
// optional rich fields to null — so a frame from an older BFF (no persona/email/…)
// still renders a valid, if leaner, card.
function normalizeLeadCall(raw: unknown): LeadCall {
  const c = raw as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    callId: str(c.callId),
    poolId: str(c.poolId),
    // A frame from a BFF that predates the field is an outbound card.
    origin: c.origin === 'inbound' ? 'inbound' : 'outbound',
    contactName: str(c.contactName),
    company: str(c.company),
    phone: str(c.phone),
    timezone: str(c.timezone),
    title: strOrNull(c.title),
    persona: strOrNull(c.persona),
    location: strOrNull(c.location),
    linkedinUrl: strOrNull(c.linkedinUrl),
    email: strOrNull(c.email),
    throxyClientId: strOrNull(c.throxyClientId),
    clientName: strOrNull(c.clientName),
    // Anything but literal true is a guess, so a sender that omits the field leaves
    // the guard armed, which is the safe direction.
    confirmed: c.confirmed === true,
    silenceGate: normalizeSilenceGate(c.silenceGate),
  };
}

// A malformed/partial config degrades to null (gate off) rather than to a config
// with holes — a gate running on NaN thresholds could fire instantly on a live call.
function normalizeSilenceGate(raw: unknown): SilenceGateConfig | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const g = raw as Record<string, unknown>;
  const pos = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  const floorMs = pos(g.floorMs);
  const quietMs = pos(g.quietMs);
  // Deliberately NOT in the required set below: an older BFF omits it and the
  // config stays valid with the lead-only tier off.
  const leadQuietMs = pos(g.leadQuietMs);
  const graceMs = pos(g.graceMs);
  const remoteMax = pos(g.remoteMax);
  const localMin = pos(g.localMin);
  if (
    typeof g.mode !== 'string' ||
    floorMs == null ||
    quietMs == null ||
    graceMs == null ||
    remoteMax == null ||
    localMin == null
  ) {
    return null;
  }
  return { mode: g.mode, floorMs, quietMs, leadQuietMs, graceMs, remoteMax, localMin };
}
